// packages/core/src/learning/core-modules/cash-flow-control.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 03: "Cash Flow Control" (tier: control)
// 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// Impact Lab links: lessons 3 & 5 deep-link to existing scenarios
// 'daily-coffee' and 'lifestyle-creep' (verified present in
// ImpactLab_impactScenarios.ts).
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'cash-flow-control--cash-flow-is-control',
	title: 'Cash flow is control',
	description:
		'You learn why cash flow is not just budgeting; it is the monthly engine that creates or destroys your options.',
	pillar: 'cash-flow-control',
	schoolsOfThought: [
		'personal-finance-basics',
		'adulting-life-skills',
		'life-design',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['cash-flow', 'control', 'budget-basics', 'money-system'],
	prerequisites: [],
	relatedLessons: ['cash-flow-control--fixed-costs-set-the-floor'],
	concepts: ['cash-flow-control', 'monthly-engine', 'money-in-money-out'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Cash flow is the movement of money through your life. Money comes in through paychecks, side work, support, refunds, or other income. Money goes out through rent, food, debt, subscriptions, transportation, family help, fun, and random life stuff. Cash flow control means you know where the money is moving before it disappears.',
		},
		{
			type: 'paragraph',
			text: 'This matters because your cash flow is the engine behind almost every money goal. You cannot build runway, pay down debt, invest, move out, travel, or build freedom if your monthly engine has no room. Your goals may be good, but if cash flow is leaking, the goals have nowhere to live.',
		},
		{
			type: 'bullet_list',
			items: [
				'Income is fuel',
				'Bills are drag',
				'Savings is stored power',
				'Debt payments are pressure',
				'The monthly gap is your control room',
			],
		},
		{
			type: 'callout',
			icon: '🎛️',
			text: 'Cash flow is not about being cheap. It is about seeing the control panel before life starts pressing buttons for you.',
		},
		{
			type: 'paragraph',
			text: 'A lot of people think budgeting means punishment. They imagine saying no to everything, tracking every chip bag, and feeling guilty for enjoying life. That is the wrong frame. Cash flow control is not “never spend.” It is “stop letting money leave without a job.” You can still have fun. You just need the fun to fit inside a system that does not rob your future options.',
		},
		{
			type: 'example',
			label: 'Real example',
			text: 'If you bring home $3,000 a month and $2,900 disappears, you have $100 of control. If you bring home $3,000 and create a $400 gap, you have four times more monthly control. That $400 can become runway, debt payoff, investing, a move-out fund, or a life upgrade you choose on purpose.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is only checking cash flow when something breaks. That turns money into emergency management. You wait until the account gets low, then panic, then cut random stuff, then loosen up again when the paycheck hits. That cycle is exhausting because it gives your money no rhythm.',
		},
		{
			type: 'paragraph',
			text: 'The better move is to treat cash flow like traffic. You do not need to control every car in the city. You need to know the main roads, the bottlenecks, and where the accidents keep happening. In money terms, that means knowing your income, fixed bills, flexible spending, debt payments, savings, and leaks.',
		},
		{
			type: 'bullet_list',
			items: [
				'Know what comes in',
				'Know what must go out',
				'Know what can flex',
				'Protect the gap',
				'Repeat every month',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Cash flow control is how your money starts obeying your goals instead of your life running on leftovers.',
		},
	],
	audioScript:
		'Cash flow is one of the most important money ideas because it is the engine of your real life. Not your dream budget. Not your perfect plan. Your actual monthly movement. Money comes in. Money goes out. The space between those two things is where your options live. If you bring home three thousand dollars and twenty-nine hundred disappears, you only have one hundred dollars of control. That one hundred has to fight for everything: savings, debt payoff, emergencies, investing, moving, travel, breathing room. But if you can create a four hundred dollar gap, now the whole month feels different. You have more control. More choices. More ability to direct money instead of just reacting. And this is where budgeting gets misunderstood. People hear “cash flow” and think it means being cheap, boring, or strict. That is not the point. Cash flow control is not “never spend.” It is “make sure the spending has a job.” If going out with friends matters to you, cool. Put it in the plan. If food convenience matters during a stressful work week, be honest about it. The problem is when money leaves with no plan, no memory, and no connection to what you actually want. Then you wake up at the end of the month wondering where everything went. Cash flow control is basically your money traffic map. You do not need to obsess over every tiny movement. You need to know the main roads: income, fixed bills, flexible spending, debt payments, savings, and leaks. Once you can see those, you can make cleaner moves. You can protect the gap. You can stop letting every paycheck reset the same chaos. Money starts becoming a system instead of a mood.',
	flashcards: [
		{
			id: 'cash-flow-control--cash-flow-is-control--fc-main',
			role: 'main_idea',
			front: 'What is cash flow?',
			back: 'Cash flow is how money moves in and out of your life each month.',
		},
		{
			id: 'cash-flow-control--cash-flow-is-control--fc-watch',
			role: 'watch_out',
			front: 'What is the budgeting trap?',
			back: 'Thinking cash flow control means punishment instead of giving each dollar a clear job.',
		},
		{
			id: 'cash-flow-control--cash-flow-is-control--fc-use',
			role: 'use_it',
			front: 'What does cash flow control protect?',
			back: 'It protects the monthly gap that funds runway, debt payoff, investing, and future options.',
		},
	],
	applyAction: {
		type: 'createBudgetPlan',
		label: 'Build your cash flow map',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'cash-flow-control--fixed-costs-set-the-floor',
	title: 'Fixed costs set the floor',
	description:
		'You learn why rent, car payments, insurance, and other fixed costs decide how much freedom your month can realistically have.',
	pillar: 'cash-flow-control',
	schoolsOfThought: [
		'personal-finance-basics',
		'frugality',
		'adulting-life-skills',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['fixed-costs', 'bills', 'rent', 'car-payment'],
	prerequisites: ['cash-flow-control--cash-flow-is-control'],
	relatedLessons: ['cash-flow-control--money-leaks-hide-in-normal-weeks'],
	concepts: ['fixed-cost-floor', 'monthly-obligations', 'cash-flow-pressure'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Fixed costs are the bills that show up whether you are motivated or not. Rent, car payments, insurance, phone plans, loan minimums, subscriptions, memberships, and anything else that repeats. These costs set the floor of your life. Before you choose anything fun, flexible, or future-focused, fixed costs already took their seat.',
		},
		{
			type: 'paragraph',
			text: 'This matters because most people over-focus on small spending while ignoring the big monthly anchors. A $6 coffee can leak money, but a $500 car payment can shape your entire month. A slightly cheaper apartment might matter more than cutting every small joy. The point is not that big expenses are always bad. The point is that fixed costs create pressure every single month.',
		},
		{
			type: 'bullet_list',
			items: [
				'Rent sets the housing floor',
				'Transportation sets the movement floor',
				'Insurance sets the protection floor',
				'Debt minimums set the past-choice floor',
				'Subscriptions set the convenience floor',
			],
		},
		{
			type: 'callout',
			icon: '🏗️',
			text: 'Fixed costs are heavy because you do not decide them once. You pay them again every month.',
		},
		{
			type: 'paragraph',
			text: 'The danger is lifestyle lock-in. You make one decision — a lease, a car, a payment plan, a phone plan, a financed purchase — and suddenly your future paychecks are already spoken for. That can be worth it if the decision truly fits your life. But if the fixed cost is too high, it quietly steals options before the month even begins.',
		},
		{
			type: 'example',
			label: 'Quick math',
			text: 'If someone brings home $3,200 a month and fixed costs are $2,400, they only have $800 left for food, gas, savings, debt extra payments, fun, and surprises. If fixed costs are $1,900 instead, they have $1,300 left. That $500 difference can become emergency runway, debt payoff, or actual breathing room.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is asking only, “Can I make the payment?” That question is too weak. A payment can be technically possible and still be too heavy. The better question is, “What does this payment do to my monthly gap?” A fixed cost does not just cost money. It reduces the number of future moves you can make.',
		},
		{
			type: 'paragraph',
			text: 'Think of fixed costs like the floor in a room. If the floor rises too high, everything above it gets squeezed. Your savings, fun, food, goals, and calm all fight for the remaining space. Lower fixed costs do not automatically make someone rich, but they give the rest of the money system more oxygen.',
		},
		{
			type: 'bullet_list',
			items: [
				'List every repeating bill',
				'Separate needs from choices',
				'Notice the heavy anchors',
				'Ask what each one does to your gap',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Your fixed costs decide how much freedom your month has before you make any new choices.',
		},
	],
	audioScript:
		'Fixed costs are the bills that show up whether you feel ready or not. Rent. Car payment. Insurance. Phone. Debt minimums. Subscriptions. Memberships. Anything repeating. These costs set the floor of your life. Before you buy food, have fun, save money, pay extra debt, or handle surprises, fixed costs already took their cut. And this is where people get distracted. They will beat themselves up over coffee while ignoring the car payment that is crushing the month. Now, small leaks matter too. But the big anchors usually decide how much oxygen your month has. Imagine two people both bring home thirty-two hundred dollars a month. One has twenty-four hundred dollars in fixed costs. The other has nineteen hundred. That five hundred dollar difference is massive. It could become runway. It could become debt payoff. It could become savings. It could become calm. Same income, different pressure. The common mistake is asking, “Can I make the payment?” That question is too low-quality. A payment can be technically possible and still be too heavy. The better question is, “What does this do to my monthly gap?” Because a fixed cost is not just one decision. It is a claim on future paychecks. A lease, a car, a payment plan, a subscription stack — these things can quietly lock in your lifestyle before you even notice. This does not mean every fixed cost is bad. Housing matters. Transportation matters. Insurance matters. Convenience can matter. But you need to know what each one is doing to your freedom. Fixed costs are like the floor in a room. If the floor rises too high, everything else gets squeezed.',
	flashcards: [
		{
			id: 'cash-flow-control--fixed-costs-set-the-floor--fc-main',
			role: 'main_idea',
			front: 'What are fixed costs?',
			back: 'Fixed costs are repeating obligations like rent, car payments, insurance, loan minimums, and subscriptions.',
		},
		{
			id: 'cash-flow-control--fixed-costs-set-the-floor--fc-watch',
			role: 'watch_out',
			front: 'What is weak about “Can I make the payment?”',
			back: 'It ignores how the payment affects your monthly gap and future options.',
		},
		{
			id: 'cash-flow-control--fixed-costs-set-the-floor--fc-use',
			role: 'use_it',
			front: 'What should you ask before adding a fixed cost?',
			back: 'Ask, “What does this payment do to my monthly gap?”',
		},
	],
	applyAction: {
		type: 'runFreedomCost',
		label: 'Test a monthly fixed cost',
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'cash-flow-control--money-leaks-hide-in-normal-weeks',
	title: 'Money leaks hide in normal weeks',
	description:
		'You learn how everyday spending leaks quietly drain your monthly gap without turning small joys into guilt.',
	pillar: 'cash-flow-control',
	schoolsOfThought: [
		'behavioral-finance',
		'frugality',
		'money-psychology-housel',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 1,
	tags: ['money-leaks', 'spending', 'daily-habits', 'cash-flow'],
	prerequisites: ['cash-flow-control--fixed-costs-set-the-floor'],
	relatedLessons: ['cash-flow-control--give-every-dollar-a-job'],
	concepts: ['money-leaks', 'invisible-spending', 'daily-friction'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Money leaks are the small, repeated expenses that feel harmless alone but heavy together. Delivery fees, random snacks, app upgrades, forgotten subscriptions, rideshare convenience, extra drinks, impulse buys, and little “I deserve it” purchases. None of them automatically ruin you. The problem is when they become invisible.',
		},
		{
			type: 'paragraph',
			text: 'Leaks matter because cash flow is usually won or lost in normal weeks. Not dramatic weeks. Normal ones. A person may have a decent income and still feel broke because too many dollars leave without being noticed. The money did not explode. It leaked.',
		},
		{
			type: 'bullet_list',
			items: [
				'Convenience leaks',
				'Subscription leaks',
				'Food leaks',
				'Boredom leaks',
				'Stress leaks',
				'Social pressure leaks',
			],
		},
		{
			type: 'callout',
			icon: '💧',
			text: 'A leak is not a purchase you enjoy on purpose. A leak is money leaving without matching what you actually care about.',
		},
		{
			type: 'paragraph',
			text: 'This distinction matters. Nisk is not here to tell you coffee is evil or eating out means failure. Sometimes convenience is worth it. Sometimes a meal out saves your sanity. Sometimes spending money on friends, dates, family, or rest is part of a good life. The problem is not spending. The problem is unconscious repetition.',
		},
		{
			type: 'example',
			label: 'Quick math',
			text: 'A $7 daily habit is about $210 a month if it happens 30 times. A $20 weekly eating-out upgrade is about $80 a month. Three forgotten $12 subscriptions are $36 a month. Together, these can quietly eat $326 of monthly cash flow before you even talk about rent, debt, or savings.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is trying to fix leaks with shame. You feel bad, cut everything, last four days, then swing back. That is not control. That is a guilt cycle. A better move is to find the leaks you barely value first. Keep the spending that genuinely improves your life. Cut or shrink the spending you forget, regret, or do only by habit.',
		},
		{
			type: 'paragraph',
			text: 'The mental model is a bucket. Your income fills the bucket. Fixed costs punch big planned holes near the bottom. Money leaks are tiny cracks along the side. One crack is not a disaster. Too many cracks mean the bucket never fills enough to create options.',
		},
		{
			type: 'bullet_list',
			items: [
				'Find what repeats',
				'Circle what you barely value',
				'Keep what matters',
				'Redirect the leak into one goal',
			],
		},
		{
			type: 'key_takeaway',
			text: 'Money leaks are not about guilt; they are about finding dollars that no longer match your real life.',
		},
	],
	audioScript:
		'Money leaks hide in normal weeks. That is what makes them dangerous. They are not always dramatic. It is not always one huge purchase that wrecks the month. Sometimes it is delivery fees, random snacks, app upgrades, subscriptions you forgot about, rideshares you did not plan for, impulse buys, and small “I deserve it” purchases after a hard day. Alone, they do not look like much. Together, they can drain the gap that was supposed to fund your goals. And we have to be careful here because this lesson is not “coffee is bad” or “never eat out.” That advice is lazy. Sometimes convenience is worth it. Sometimes spending on food, friends, rest, and fun is part of a good life. A purchase you enjoy on purpose is not the same as a leak. A leak is money leaving without matching what you actually care about. That is the key. Let’s do quick math. A seven dollar daily habit is about two hundred ten dollars a month. A twenty dollar weekly eating-out upgrade is around eighty dollars a month. Three forgotten twelve dollar subscriptions are thirty-six dollars a month. Put those together and you are over three hundred dollars of monthly cash flow. That could be runway. Debt payoff. A move-out fund. A real experience you care about. The mistake is trying to fix leaks with shame. You cut everything, feel restricted, break the plan, and then feel worse. Skip that cycle. Look for the spending you barely value. Keep what matters. Redirect what does not. Cash flow control gets much easier when your money stops leaking into things you do not even remember.',
	flashcards: [
		{
			id: 'cash-flow-control--money-leaks-hide-in-normal-weeks--fc-main',
			role: 'main_idea',
			front: 'What is a money leak?',
			back: 'A money leak is repeated spending that leaves without matching what you actually value.',
		},
		{
			id: 'cash-flow-control--money-leaks-hide-in-normal-weeks--fc-watch',
			role: 'watch_out',
			front: 'Why is shame bad for fixing leaks?',
			back: 'Shame often creates a strict plan that breaks quickly, then restarts the guilt cycle.',
		},
		{
			id: 'cash-flow-control--money-leaks-hide-in-normal-weeks--fc-use',
			role: 'use_it',
			front: 'How do you handle money leaks?',
			back: 'Find repeated spending, keep what matters, cut what you barely value, and redirect it to a goal.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'See a daily leak over time',
		scenarioId: 'daily-coffee',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'cash-flow-control--give-every-dollar-a-job',
	title: 'Give every dollar a job',
	description:
		'You learn how to assign money before it drifts into impulse, stress, or leftovers.',
	pillar: 'cash-flow-control',
	schoolsOfThought: [
		'personal-finance-basics',
		'rich-life-ramit',
		'frugality',
	],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['budgeting', 'spending-plan', 'dollar-job', 'cash-flow'],
	prerequisites: ['cash-flow-control--money-leaks-hide-in-normal-weeks'],
	relatedLessons: ['cash-flow-control--adjust-without-hating-your-life'],
	concepts: ['dollar-jobs', 'intentional-budgeting', 'planned-spending'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Giving every dollar a job means your money has a purpose before it disappears. Some dollars pay rent. Some buy food. Some handle debt. Some build runway. Some buy fun. Some protect future you. The point is not to make money strict. The point is to make it less random.',
		},
		{
			type: 'paragraph',
			text: 'Leftover budgeting usually fails because it waits too long. People say, “I’ll save whatever is left at the end of the month.” But if money has no job, life will usually find one for it. Stress, convenience, boredom, social pressure, and random spending are very good at hiring unemployed dollars.',
		},
		{
			type: 'bullet_list',
			items: [
				'Bills get a job',
				'Food gets a job',
				'Fun gets a job',
				'Savings gets a job',
				'Debt payoff gets a job',
				'Future plans get a job',
			],
		},
		{
			type: 'callout',
			icon: '🧾',
			text: 'If a dollar has no job, impulse will probably give it one.',
		},
		{
			type: 'paragraph',
			text: 'This does not mean you need fifty categories or a perfect spreadsheet. A simple plan can work better than a complicated one. You need enough structure to protect the important stuff and enough flexibility that real life does not break the system every week.',
		},
		{
			type: 'example',
			label: 'Simple split',
			text: 'If you bring home $2,800, you might assign $1,700 to fixed bills, $500 to food and transportation, $200 to debt extra payments, $200 to runway, and $200 to fun or personal spending. The exact numbers are not universal. The habit is the lesson: decide before the money drifts.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is treating fun money like failure. That creates a fake plan. If you know you are going to spend on friends, hobbies, dates, food, or small joys, then plan for it. A realistic fun category is stronger than a fantasy budget that pretends you are not human.',
		},
		{
			type: 'paragraph',
			text: 'The mental model is a team roster. Your dollars are players. If nobody has a position, the game gets chaotic. You do not need every play scripted perfectly, but you do need to know who is defending the rent, who is building runway, who is attacking debt, and who is allowed to enjoy the weekend.',
		},
		{
			type: 'bullet_list',
			items: [
				'Protect needs first',
				'Assign future-you money early',
				'Include realistic fun',
				'Review and adjust',
			],
		},
		{
			type: 'key_takeaway',
			text: 'A budget works better when it gives your money jobs, not guilt.',
		},
	],
	audioScript:
		'Giving every dollar a job sounds strict, but it is actually freeing. It means your money has a purpose before life grabs it. Some dollars pay rent. Some dollars buy food. Some handle gas. Some attack debt. Some build runway. Some are for fun. Some are for future you. The point is not to control every breath you take. The point is to stop letting money wander off with no plan. Most people use leftover budgeting without realizing it. They say, “I’ll save whatever is left.” But the end of the month comes, and somehow there is nothing left. That is not because they are broken. It is because unassigned money gets hired by impulse. Stress hires it. Boredom hires it. Convenience hires it. Friends hire it. Random little purchases hire it. So instead of waiting to see what survives, assign the important dollars early. Bills get a job. Food gets a job. Savings gets a job. Debt payoff gets a job. Fun gets a job too. That last part matters. A plan with no fun is usually fake. If you know you are going to go out, buy food, date, see friends, or enjoy a hobby, put it in the plan. Realistic spending beats fantasy discipline. Think of your dollars like a team. If nobody has a position, the game turns chaotic. You do not need a perfect playbook, but you need to know who is defending rent, who is building runway, who is reducing debt, and who is allowed to enjoy life. That is cash flow control. Not guilt. Not punishment. Jobs.',
	flashcards: [
		{
			id: 'cash-flow-control--give-every-dollar-a-job--fc-main',
			role: 'main_idea',
			front: 'What does “give every dollar a job” mean?',
			back: 'It means assigning your money a purpose before it drifts into random spending.',
		},
		{
			id: 'cash-flow-control--give-every-dollar-a-job--fc-watch',
			role: 'watch_out',
			front: 'Why does leftover budgeting fail?',
			back: 'Waiting to save what is left often fails because unassigned money gets spent first.',
		},
		{
			id: 'cash-flow-control--give-every-dollar-a-job--fc-use',
			role: 'use_it',
			front: 'What categories should get jobs?',
			back: 'Needs, savings, debt, future plans, and realistic fun should all have clear jobs.',
		},
	],
	applyAction: {
		type: 'createBudgetPlan',
		label: 'Assign your next paycheck',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'cash-flow-control--adjust-without-hating-your-life',
	title: 'Adjust without hating your life',
	description:
		'You learn how to improve cash flow by making targeted changes instead of swinging between restriction and chaos.',
	pillar: 'cash-flow-control',
	schoolsOfThought: ['rich-life-ramit', 'behavioral-finance', 'life-design'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['budget-adjustment', 'sustainability', 'spending', 'lifestyle'],
	prerequisites: ['cash-flow-control--give-every-dollar-a-job'],
	relatedLessons: ['cash-flow-control--cash-flow-is-control'],
	concepts: [
		'sustainable-adjustment',
		'spending-tradeoffs',
		'cash-flow-tuning',
	],
	blocks: [
		{
			type: 'paragraph',
			text: 'Cash flow control only works if it survives your real life. A plan that looks perfect but makes you miserable is not strong. It is fragile. You might follow it for a week, then snap back harder because the plan ignored your actual needs, stress, habits, and social life.',
		},
		{
			type: 'paragraph',
			text: 'The better approach is targeted adjustment. You do not need to cut everything. You need to find the few changes that create the most room with the least resentment. Sometimes that means cutting a leak. Sometimes it means lowering a fixed cost. Sometimes it means earning a little more. Sometimes it means choosing what stays because it genuinely matters.',
		},
		{
			type: 'bullet_list',
			items: [
				'Cut what you barely value',
				'Protect what keeps you sane',
				'Reduce one heavy fixed cost if possible',
				'Automate one future-you move',
				'Increase income when cutting is not enough',
			],
		},
		{
			type: 'callout',
			icon: '🔧',
			text: 'Sustainable control is not maximum restriction. It is the cleanest adjustment you can repeat.',
		},
		{
			type: 'paragraph',
			text: 'This is why “just spend less” is incomplete advice. Some people have real leaks. Some people have an income problem. Some people have fixed costs that are too heavy. Some people are supporting family. Some people are in a temporary rough patch. Cash flow control should tell the truth, not shame everyone with the same answer.',
		},
		{
			type: 'example',
			label: 'Two adjustments',
			text: 'One person saves $180 a month by canceling unused subscriptions, packing lunch twice a week, and setting a food delivery limit. Another person has already cut the small stuff, so the better move is negotiating a raise, finding weekend income, or changing a major fixed cost over time. Same goal. Different pressure point.',
		},
		{
			type: 'paragraph',
			text: 'The common mistake is trying to fix a structural problem with tiny cuts. If rent, car payments, debt minimums, and insurance already eat most income, skipping coffee will not save the system. It may help a little, but the real work may be bigger: income, housing, transportation, debt strategy, or time.',
		},
		{
			type: 'paragraph',
			text: 'The mental model is tuning, not punishment. A guitar sounds wrong when one string is too tight or too loose. Your money can be the same. You adjust the strings: fixed costs, flexible spending, savings, income, debt, and fun. The goal is not to make life silent. It is to make the system playable.',
		},
		{
			type: 'bullet_list',
			items: [
				'Find the pressure point',
				'Choose one adjustment',
				'Make it repeatable',
				'Watch the monthly gap',
			],
		},
		{
			type: 'key_takeaway',
			text: 'The best cash flow change is the one that creates room without making your life so miserable that you quit.',
		},
	],
	audioScript:
		'Cash flow control has to survive your real life. That sounds obvious, but a lot of money plans ignore it. They look clean on paper and then collapse because they make life miserable. Cut everything. No fun. No eating out. No flexibility. No mistakes. That kind of plan can feel powerful for a few days, but it often snaps back into chaos. The better move is targeted adjustment. You are not trying to become the most restricted version of yourself. You are trying to create more room in a way you can repeat. That might mean canceling subscriptions you barely use. It might mean setting a limit on delivery. It might mean reducing a fixed cost. It might mean earning extra income because there is not enough left to cut. It might mean protecting one category because it genuinely keeps you sane. This is why “just spend less” is weak advice. Some people have leaks. Some people have an income problem. Some people have fixed costs that are too high. Some people are helping family. Some people are dealing with debt pressure. Cash flow control should tell the truth about the pressure point. Let’s say one person frees up one hundred eighty dollars a month by canceling unused subscriptions, packing lunch twice a week, and limiting delivery. Great. Another person has already cut the easy stuff, and the real issue is rent, car payment, or income. For them, tiny cuts may help, but they will not fix the structure. Think of this as tuning, not punishment. You adjust the strings until the system becomes playable. The goal is not to hate your life. The goal is to create room you can actually keep.',
	flashcards: [
		{
			id: 'cash-flow-control--adjust-without-hating-your-life--fc-main',
			role: 'main_idea',
			front: 'What is targeted adjustment?',
			back: 'Targeted adjustment means changing the few cash flow areas that create the most room with the least resentment.',
		},
		{
			id: 'cash-flow-control--adjust-without-hating-your-life--fc-watch',
			role: 'watch_out',
			front: 'Why can extreme restriction fail?',
			back: 'It often ignores real needs and creates a plan so miserable that you quit.',
		},
		{
			id: 'cash-flow-control--adjust-without-hating-your-life--fc-use',
			role: 'use_it',
			front: 'How do you improve cash flow sustainably?',
			back: 'Find the pressure point, choose one repeatable adjustment, and watch how it changes your monthly gap.',
		},
	],
	applyAction: {
		type: 'openImpactScenario',
		label: 'Test lifestyle creep',
		scenarioId: 'lifestyle-creep',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'cash-flow-control',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--cq--q1',
			question: 'What is cash flow?',
			options: [
				'Only the amount in your savings account',
				'The movement of money in and out of your life',
				'Your credit score trend',
				'The value of everything you own',
			],
			correctIndex: 1,
			explanation:
				'Cash flow is money coming in, money going out, and the room left between them.',
		},
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--cq--q2',
			question: 'Why does cash flow control matter?',
			options: [
				'It makes every purchase wrong',
				'It creates the monthly room needed for runway, debt payoff, investing, and goals',
				'It replaces the need for income',
				'It guarantees financial freedom',
			],
			correctIndex: 1,
			explanation:
				'Cash flow is the monthly engine behind almost every money goal.',
		},
		{
			type: 'true_false',
			id: 'cash-flow-control--cq--q3',
			question: 'Cash flow control means never spending money on fun.',
			correct: false,
			explanation:
				'The module frames cash flow control as intentional spending, not punishment.',
		},
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--cq--q4',
			question: 'What is the danger of high fixed costs?',
			options: [
				'They only matter once a year',
				'They reduce your monthly gap before you make new choices',
				'They automatically build wealth',
				'They make small spending irrelevant forever',
			],
			correctIndex: 1,
			explanation:
				'Fixed costs repeat and claim future income, which can squeeze savings, flexibility, and calm.',
		},
		{
			type: 'scenario',
			id: 'cash-flow-control--cq--q5',
			scenario:
				'Dana brings home $3,200 per month. Their fixed costs are $2,450 before food, gas, savings, or fun.',
			question: 'What is the best read?',
			options: [
				'Dana has plenty of flexibility because income is above $3,000',
				'Dana’s fixed-cost floor is high and may squeeze the rest of the month',
				'Dana should only focus on coffee purchases',
				'Dana has no need to track anything',
			],
			correctIndex: 1,
			explanation:
				'Fixed costs set the floor. A high floor leaves less room for everything else.',
		},
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--cq--q6',
			question: 'What is a money leak?',
			options: [
				'Any spending that brings joy',
				'Repeated spending that leaves without matching what you value',
				'A planned rent payment',
				'A paycheck deposit',
			],
			correctIndex: 1,
			explanation:
				'A leak is not every purchase. It is unconscious or low-value repetition.',
		},
		{
			type: 'scenario',
			id: 'cash-flow-control--cq--q7',
			scenario:
				'Leo spends $9 most weekdays on convenience food and does not enjoy it much anymore.',
			question: 'What is the best cash flow move?',
			options: [
				'Feel guilty and cut all food spending',
				'Notice the repeated low-value leak and redirect some of it',
				'Ignore it because small money never matters',
				'Cancel rent instead',
			],
			correctIndex: 1,
			explanation:
				'The point is to find repeated spending that no longer matches Leo’s real priorities.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--final--q1',
			question: 'Which statement best captures Module 3?',
			options: [
				'Cash flow control is about making money less random',
				'Cash flow control means never enjoying life',
				'Cash flow control is only for high earners',
				'Cash flow control is the same as net worth',
			],
			correctIndex: 0,
			explanation:
				'The module teaches cash flow as a system for directing money toward options.',
		},
		{
			type: 'true_false',
			id: 'cash-flow-control--final--q2',
			question:
				'A budget can include fun money and still be responsible.',
			correct: true,
			explanation:
				'Realistic planned spending is stronger than a fantasy budget that ignores normal life.',
		},
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--final--q3',
			question: 'What does the monthly gap make possible?',
			options: [
				'Only shopping',
				'Runway, debt payoff, investing, and future plans',
				'Ignoring fixed costs',
				'Guaranteed returns',
			],
			correctIndex: 1,
			explanation: 'The monthly gap is the room that can fund progress.',
		},
		{
			type: 'scenario',
			id: 'cash-flow-control--final--q4',
			scenario:
				'A person brings home $3,000, has $2,100 in fixed costs, and spends the rest randomly before the month ends.',
			question: 'What is the clearest problem?',
			options: [
				'They have no income',
				'Their money needs clearer jobs before it drifts',
				'Fixed costs are zero',
				'The only solution is to never spend on fun',
			],
			correctIndex: 1,
			explanation:
				'Unassigned money often gets spent by impulse, stress, or convenience.',
		},
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--final--q5',
			question: 'What is the weak question before taking on a payment?',
			options: [
				'What does this do to my monthly gap?',
				'Can I technically make the payment?',
				'What future option does this reduce?',
				'Is this worth the tradeoff?',
			],
			correctIndex: 1,
			explanation:
				'“Can I make the payment?” may ignore how much pressure the payment adds.',
		},
		{
			type: 'scenario',
			id: 'cash-flow-control--final--q6',
			scenario:
				'Mia notices $280 per month going to subscriptions, delivery fees, and impulse buys she barely remembers.',
			question: 'What is the best interpretation?',
			options: [
				'All spending is bad',
				'Mia found possible cash flow room',
				'Mia should ignore it because it is not rent',
				'Mia should feel ashamed',
			],
			correctIndex: 1,
			explanation:
				'Low-value repeated spending can become room for runway, debt, savings, or chosen enjoyment.',
		},
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--final--q7',
			question: 'Why can leftover budgeting fail?',
			options: [
				'It assigns savings first',
				'It waits to save what survives after impulse and life spend the money',
				'It makes fixed costs disappear',
				'It tracks too clearly',
			],
			correctIndex: 1,
			explanation:
				'If savings waits until the end, unassigned money often disappears first.',
		},
		{
			type: 'true_false',
			id: 'cash-flow-control--final--q8',
			question:
				'Sustainable cash flow control should look for repeatable changes, not only maximum restriction.',
			correct: true,
			explanation:
				'A plan that creates room but makes life miserable is fragile.',
		},
		{
			type: 'scenario',
			id: 'cash-flow-control--final--q9',
			scenario:
				'Chris has already cut small leaks, but rent, car payment, insurance, and debt minimums eat most of their income.',
			question: 'What does Module 3 suggest?',
			options: [
				'Keep cutting tiny joys and ignore structure',
				'Recognize that the pressure point may be fixed costs, income, or debt structure',
				'Stop tracking cash flow',
				'Assume the situation is hopeless',
			],
			correctIndex: 1,
			explanation:
				'Sometimes the problem is structural, not just small spending.',
		},
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--final--q10',
			question: 'What is the best mental model for adjusting cash flow?',
			options: [
				'Punishment',
				'Tuning a system',
				'Hiding from numbers',
				'Winning a moral contest',
			],
			correctIndex: 1,
			explanation:
				'The module frames adjustment as tuning fixed costs, flexible spending, savings, income, debt, and fun.',
		},
		{
			type: 'scenario',
			id: 'cash-flow-control--final--q11',
			scenario:
				'Renee wants to free up $150 per month without hating her life.',
			question: 'Which plan fits the module best?',
			options: [
				'Cut every enjoyable category immediately',
				'Find low-value repeated spending and redirect it first',
				'Refuse to track anything',
				'Add a new fixed payment',
			],
			correctIndex: 1,
			explanation:
				'The best first adjustment often targets spending that creates little value but repeats often.',
		},
		{
			type: 'multiple_choice',
			id: 'cash-flow-control--final--q12',
			question: 'What is the main purpose of giving dollars jobs?',
			options: [
				'To create guilt around every dollar',
				'To assign money a purpose before it disappears',
				'To make the plan impossible to change',
				'To avoid all flexible spending',
			],
			correctIndex: 1,
			explanation:
				'Assigning jobs makes money intentional instead of random.',
		},
	],
};

export const CASH_FLOW_CONTROL_MODULE: LearningModule = {
	id: 'cash-flow-control',
	title: 'Cash Flow Control',
	description:
		'Cash flow is the monthly engine behind every goal. Learn to see what comes in, what goes out, where it leaks, and how to give every dollar a job without hating your life.',
	emoji: '🎛️',
	accentColor: '#c8ff00',
	tier: 'control',
	pillar: 'cash-flow-control',
	order: 3,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
